import React, { useEffect } from "react";

import {
  DataGridPro,
  gridClasses,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import {
  styled,
  alpha,
  Radio,
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Box,
  Typography,
} from "@mui/material";
import {
  org_hitter_columns,
  org_hitter_columns_mobile,
  org_pitcher_columns,
  org_pitcher_columns_mobile,
} from "./utils/ColumnDefs";
import { useMobileZoom } from "./utils/useMobileZoom";
import { useHeaderFilters } from "./utils/useHeaderFilters";

const ODD_OPACITY = 0.2;

const StripedDataGrid = styled(DataGridPro)(({ theme }) => ({
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: theme.palette.grey[200],
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
      "@media (hover: none)": { backgroundColor: "transparent" },
    },
    "&.Mui-selected": {
      backgroundColor: alpha(
        theme.palette.primary.main,
        ODD_OPACITY + theme.palette.action.selectedOpacity
      ),
    },
  },
  [`& .${gridClasses.row}.odd`]: {
    backgroundColor: "white",
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
      "@media (hover: none)": { backgroundColor: "transparent" },
    },
    "&.Mui-selected": {
      backgroundColor: alpha(
        theme.palette.primary.main,
        ODD_OPACITY + theme.palette.action.selectedOpacity
      ),
    },
  },
}));

const LEVELS = ["All", "AAA", "AA", "A+", "A", "Rk"];

export default function OrgLeaderboard(props: any) {
  const apiRef = useGridApiRef();

  const [position, setPosition] = React.useState("hitters");
  const [level, setLevel] = React.useState("All");
  const [season, setSeason] = React.useState("2026");
  // Sample-size floor: plate appearances for hitters, innings for pitchers.
  const [paQualifier, setPaQualifier] = React.useState("50");
  const [ipQualifier, setIpQualifier] = React.useState("10");
  const isPitchers = position === "pitchers";
  const qualifier = isPitchers ? ipQualifier : paQualifier;
  const [orgData, setOrgData] = React.useState<Array<any>>([]);
  const [loading, setLoading] = React.useState(true);

  const { gridSx, wrapperSx, zoomButtons } = useMobileZoom(props.isDesktop);
  const { headerFilters, headerFilterButton } = useHeaderFilters();

  const gutter = props.isDesktop ? "30px" : "10px";
  const selectSize = props.isDesktop ? "medium" : "small";

  const columns =
    position === "hitters"
      ? props.isDesktop
        ? org_hitter_columns
        : org_hitter_columns_mobile
      : props.isDesktop
        ? org_pitcher_columns
        : org_pitcher_columns_mobile;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const path =
      "/org-leaders/" + position + "/" + season + "/" + level + "/" + qualifier + "/0/99";
    fetch(props.prod ? "https://oriolebird.pythonanywhere.com" + path : path)
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled) setOrgData(data.data ?? []);
      })
      .catch(() => {
        if (!cancelled) setOrgData([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [position, season, level, qualifier, props.prod]);

  function CustomToolbar() {
    return (
      <GridToolbarContainer sx={{ backgroundColor: "#FFFFFF" }}>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        {headerFilterButton}
        <GridToolbarDensitySelector />
        <GridToolbarExport />
        {zoomButtons}
        <GridToolbarQuickFilter
          sx={{ marginLeft: "auto", width: props.isDesktop ? 400 : 200 }}
        />
      </GridToolbarContainer>
    );
  }

  return (
    <div style={{ width: "100%" }}>
      <title>Organization Aggregates | Prospect Savant</title>
      <meta
        name="description"
        content="Minor league stats aggregated to the parent organization across every level - read an org's development philosophy at a glance."
      />
      <meta name="title" content="Organization Aggregates | Prospect Savant" />
      <meta
        property="og:description"
        content="Minor league stats aggregated to the parent organization across every level."
      />
      <meta property="og:title" content="Organization Aggregates | Prospect Savant" />

      <Box sx={{ mt: "10px", mx: gutter }}>
        <Typography variant={props.isDesktop ? "h6" : "subtitle1"} sx={{ fontWeight: "bold" }}>
          Organization Aggregates
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "#3D5A80", maxWidth: "1000px", fontSize: props.isDesktop ? undefined : 12 }}
        >
          {props.isDesktop
            ? "Every player-season-level row rolled up to the parent MLB org. Rate stats are sample-weighted (PA, batters faced, pitches or batted balls depending on the stat), so a system's collective approach - swing decisions, launch angle, velocity, how aggressively it promotes - shows through."
            : "Every player-season-level row rolled up to the parent MLB org, with rate stats sample-weighted."}
        </Typography>
      </Box>

      <Box
        sx={{
          mt: "10px",
          mb: "-20px",
          ml: gutter,
          mr: gutter,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          columnGap: "10px",
        }}
      >
        <FormControl sx={{ flexBasis: props.isDesktop ? "auto" : "100%" }}>
          <FormLabel sx={{ fontSize: props.isDesktop ? undefined : 13 }}>Players</FormLabel>
          <RadioGroup row name="org-position-group">
            <FormControlLabel
              value="hitters"
              control={
                <Radio
                  size={props.isDesktop ? "medium" : "small"}
                  checked={position === "hitters"}
                  onChange={(e) => setPosition(e.target.value)}
                  value="hitters"
                />
              }
              label="Hitters"
              slotProps={{ typography: { fontSize: props.isDesktop ? undefined : 13 } }}
            />
            <FormControlLabel
              value="pitchers"
              control={
                <Radio
                  size={props.isDesktop ? "medium" : "small"}
                  checked={position === "pitchers"}
                  onChange={(e) => setPosition(e.target.value)}
                  value="pitchers"
                />
              }
              label="Pitchers"
              slotProps={{ typography: { fontSize: props.isDesktop ? undefined : 13 } }}
            />
          </RadioGroup>
        </FormControl>

        <FormControl size={selectSize}>
          <InputLabel id="org-level-label">Level</InputLabel>
          <Select
            labelId="org-level-label"
            value={level}
            label="Level"
            onChange={(e: SelectChangeEvent) => setLevel(e.target.value)}
            sx={{ width: props.isDesktop ? "100px" : "80px" }}
          >
            {LEVELS.map((l) => (
              <MenuItem key={l} value={l}>
                {l}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size={selectSize}>
          <InputLabel id="org-season-label">Season</InputLabel>
          <Select
            labelId="org-season-label"
            value={season}
            label="Season"
            onChange={(e: SelectChangeEvent) => setSeason(e.target.value)}
            sx={{ width: props.isDesktop ? "100px" : "90px" }}
          >
            <MenuItem value={"2026"}>2026</MenuItem>
            <MenuItem value={"2025"}>2025</MenuItem>
            <MenuItem value={"2024"}>2024</MenuItem>
            <MenuItem value={"2023"}>2023</MenuItem>
          </Select>
        </FormControl>

        <FormControl size={selectSize}>
          <InputLabel id="org-qual-label">{isPitchers ? "Min IP" : "Min PA"}</InputLabel>
          <Select
            labelId="org-qual-label"
            value={qualifier}
            label={isPitchers ? "Min IP" : "Min PA"}
            onChange={(e: SelectChangeEvent) =>
              (isPitchers ? setIpQualifier : setPaQualifier)(e.target.value)
            }
            sx={{ width: props.isDesktop ? "115px" : "105px" }}
          >
            {(isPitchers ? [1, 5, 10, 25, 50, 100] : [1, 25, 50, 100, 200, 300]).map((v) => (
              <MenuItem key={v} value={String(v)}>
                {v}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ mt: "30px", mx: gutter }}>
        <span
          style={{
            fontWeight: "bold",
            color: "#DC707A",
            fontSize: props.isDesktop ? undefined : 11,
          }}
        >
          {props.isDesktop
            ? '** Statcast tracking is not league-wide: exit velocity, expected stats, barrels and pitch velocity come only from AAA, the Florida State League (10 orgs) and part of the complex leagues. Those columns are blank where no data exists, so a cross-level "All" view mixes different underlying samples - pick a single level to compare orgs on identical footing. Rate, run-prevention and batted-ball columns are available at every level. **'
            : "** Statcast columns (EV, expected stats, barrels, velocity) cover only AAA, the FSL and part of the complex leagues. Pick a single level to compare orgs on equal footing. **"}
        </span>
      </Box>

      <Box sx={wrapperSx}>
        <StripedDataGrid
          apiRef={apiRef}
          loading={loading}
          sx={{
            height: "80vh",
            width: props.isDesktop ? "99%" : "100%",
            marginTop: "20px",
            marginLeft: "auto",
            marginRight: "auto",
            ...gridSx,
          }}
          rows={orgData}
          columns={columns}
          getRowId={(row) => row.id}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
          }
          disableColumnResize={false}
          headerFilters={headerFilters}
          slots={{ toolbar: CustomToolbar }}
          initialState={{
            sorting: { sortModel: [{ field: "players", sort: "desc" }] },
            density: "compact",
            pinnedColumns: { left: ["MLB_AbbName"] },
          }}
        />
      </Box>
    </div>
  );
}
